import { useEffect, useState } from "react";
import { supabase } from "../supabase";

type Mode = "signin" | "signup";

// The policy pages live on the MARKETING site (broadside.social), a separate
// Netlify deploy. This app is served from app.broadside.social behind an SPA
// catch-all, so a relative "/privacy.html" resolves to app.broadside.social and
// the redirect hands back the React shell — a 200 that is not the policy. The
// links must be absolute or the III.A.2 agreement below points at nothing.
// (App.tsx's footer links carry the same absolute URLs, for the same reason.)
const TERMS_URL = "https://broadside.social/terms.html";
const PRIVACY_URL = "https://broadside.social/privacy.html";

export function Login() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Explicit agreement to the policies (clickwrap). Signup is gated on this so
  // consent is a deliberate act, not merely implied by clicking the button.
  const [agreed, setAgreed] = useState(false);

  // Two-factor challenge state. When `mfaFactorId` is set the signed-in session
  // is at aal1 with a verified TOTP factor, so we present the 6-digit step
  // instead of the app. Users with no factor never reach this — `checkForMfa`
  // resolves to no factor and the App's auth listener shows the dashboard.
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  // After a password sign-in (or a reload while at aal1), decide whether a
  // second factor is still owed and, if so, which factor to challenge. Returns
  // true when a challenge step is now showing.
  async function checkForMfa(): Promise<boolean> {
    // Fully defensive: if the MFA API is unavailable (older SDK, or a disabled
    // project) this must never throw — the sign-in flow proceeds unchanged.
    try {
      const mfa = supabase.auth.mfa;
      if (!mfa) return false;
      const { data: aal } = await mfa.getAuthenticatorAssuranceLevel();
      if (!aal || aal.nextLevel !== "aal2" || aal.nextLevel === aal.currentLevel) return false;
      const { data: factors } = await mfa.listFactors();
      const factor = factors?.totp?.[0];
      if (!factor) return false;
      setMfaFactorId(factor.id);
      setMfaCode("");
      return true;
    } catch {
      return false;
    }
  }

  // Handle the reload-during-MFA case: this component is re-mounted with an
  // existing aal1 session, so surface the challenge rather than the sign-in form.
  useEffect(() => {
    void checkForMfa();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "signup") {
        // Defense in depth: the button is already disabled until `agreed`, but
        // never create an account without the recorded consent.
        if (!agreed) {
          setError("Please agree to the Terms of Service and Privacy Policy to continue.");
          return;
        }
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        // If email confirmation is on, there's no session yet.
        if (!data.session) {
          setNotice(
            "Account created. We’ve emailed you a confirmation link — click it, then sign in here.",
          );
          setMode("signin");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // A verified second factor turns the sign-in into a two-step flow: show
        // the code challenge here before the App proceeds. (No factor → no-op.)
        await checkForMfa();
      }
      // On success the App's auth listener swaps to the dashboard.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  // Verify the 6-digit TOTP code, upgrading the session to aal2. On success
  // Supabase emits a fresh session and the App's auth listener shows the
  // dashboard; a wrong code is surfaced for a retry.
  async function submitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaFactorId) return;
    setBusy(true);
    setError(null);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: mfaCode.trim(),
      });
      if (vErr) throw vErr;
      // Session is now aal2 — the App gate clears itself.
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't match. Try again.");
      setMfaCode("");
    } finally {
      setBusy(false);
    }
  }

  // Abandon the challenge: sign back out so the sign-in form returns.
  async function cancelMfa() {
    setError(null);
    setMfaFactorId(null);
    setMfaCode("");
    await supabase.auth.signOut();
  }

  // One-click Google sign-in / sign-up via Supabase's OAuth provider. This
  // redirects to Google's consent screen and returns to this origin, where the
  // App's auth listener picks up the new session. Works for both new and
  // returning users (Google creates the account on first use). Consent to the
  // policies is carried by the notice under the button (III.A.2): agreeing is a
  // precondition of continuing, shown at the point of the action.
  async function signInWithGoogle() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
      // Success redirects away; leave `busy` set so the button stays disabled
      // through the navigation.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start Google sign-in.");
      setBusy(false);
    }
  }

  // Send a password-reset email. The reset link returns the user to this app,
  // where Supabase's recovery session lets them set a new password.
  async function forgotPassword() {
    if (!email) {
      setError("Enter your email above first, then tap “Forgot password?”.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setNotice("Check your email for a link to reset your password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send a reset email.");
    } finally {
      setBusy(false);
    }
  }

  // Two-factor challenge: shown after a password sign-in when a verified factor
  // exists, standing in for the dashboard until the code is verified.
  if (mfaFactorId) {
    return (
      <div className="center">
        <form className="card login" onSubmit={submitMfa}>
          <img className="brand-mark big" src="/logo.png" alt="" width={56} height={56} />
          <h1>Two-factor</h1>
          <p className="muted">Enter the 6-digit code from your authenticator app.</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            aria-label="Six-digit authentication code"
            value={mfaCode}
            autoFocus
            onChange={(e) => setMfaCode(e.target.value)}
          />
          {error && <p className="error">{error}</p>}
          <button className="primary" disabled={busy || mfaCode.trim().length < 6}>
            {busy ? "…" : "Verify"}
          </button>
          <p className="muted small" style={{ marginTop: 14 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                void cancelMfa();
              }}
            >
              Cancel
            </a>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="center">
      <form className="card login" onSubmit={submit}>
        <img className="brand-mark big" src="/logo.png" alt="" width={56} height={56} />
        <h1>Broadside</h1>
        <p className="muted">
          {mode === "signin" ? "Sign in to publish across your platforms." : "Create your account."}
        </p>

        <button
          type="button"
          className="btn-google"
          onClick={() => void signInWithGoogle()}
          disabled={busy}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.33A9 9 0 0 0 9 18z"
            />
            <path
              fill="#FBBC05"
              d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.02-2.33z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58z"
            />
          </svg>
          Continue with Google
        </button>
        {/* The account-creation consent (Policies III.A.2) belongs only on the
            create-account path — the sign-in path is kept free of policy
            clutter, mirroring the email clickwrap below. A returning Google user
            signing in has already agreed at creation. */}
        {mode === "signup" && (
          <p className="muted small auth-consent">
            By continuing with Google, you agree to the{" "}
            <a href={TERMS_URL} target="_blank" rel="noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        )}
        <div className="auth-divider">
          <span>or</span>
        </div>

        <input
          type="email"
          placeholder="Email"
          aria-label="Email address"
          value={email}
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          aria-label="Password"
          value={password}
          autoComplete={mode === "signin" ? "current-password" : "new-password"}
          onChange={(e) => setPassword(e.target.value)}
        />
        {mode === "signin" && (
          <p className="muted small forgot-row">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                void forgotPassword();
              }}
            >
              Forgot password?
            </a>
          </p>
        )}
        {error && <p className="error">{error}</p>}
        {notice && (
          <div className="auth-notice" role="status">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <span>{notice}</span>
          </div>
        )}
        {/* YouTube API Services Developer Policies III.A.2 — users must agree to
            a privacy policy before they can reach the client's functionality.
            Creating the account is that moment, so the explicit consent sits
            directly above the button that creates it. A required checkbox
            (clickwrap) makes the agreement a deliberate act, which is far more
            enforceable than an implied "by clicking, you agree" line. */}
        {mode === "signup" && (
          <label className="muted small signup-agreement">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <a href={TERMS_URL} target="_blank" rel="noreferrer">
                Terms of Service
              </a>{" "}
              and the{" "}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </label>
        )}
        <button
          className="primary"
          disabled={busy || !email || !password || (mode === "signup" && !agreed)}
        >
          {busy ? "…" : mode === "signin" ? "Sign in" : "Sign up"}
        </button>
        <p className="muted small" style={{ marginTop: 14 }}>
          {mode === "signin" ? "New here? " : "Already have an account? "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setError(null);
              setNotice(null);
              setAgreed(false);
              setMode(mode === "signin" ? "signup" : "signin");
            }}
          >
            {mode === "signin" ? "Create an account" : "Sign in"}
          </a>
        </p>
      </form>
    </div>
  );
}
